import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  Bell,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Filter,
  Globe,
  Loader2,
  Megaphone,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  Trash2,
  UserRound,
  UsersRound,
  X,
} from 'lucide-react';

import {
  AdminLead,
  AdminLeadQuery,
  LeadMetrics,
  exportAdminLeadsCsv,
  fetchAdminLeads,
  fetchLeadMetrics,
  previewFollowUpReminders,
  sendFollowUpReminders,
} from '@/api/adminLeads';
import LeadCard from '@/components/leads/LeadCard';
import { PageHeader } from '@/components/layouts/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { StatCard } from '@/components/ui/StatCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { LeadStageType, ServiceType, useLeads } from '@/context/LeadContext';
import { SERVICE_OPTIONS } from '@/lib/serviceCatalog';
import { cn } from '@/lib/utils';

type QueueFilter = 'all' | 'unassigned' | 'today' | 'stale' | 'duplicates';
type SourceFilter = 'all' | 'facebook_api' | 'facebook_sheets' | 'facebook_leads' | 'web';
type SortOrder = 'newest' | 'oldest';

const VALID_STAGES: LeadStageType[] = ['Νέο', 'σεΕκκρεμότητα', 'ήθελεΠληροφορίες', 'έκλεισεΗΔουλειά'];
const VALID_QUEUES: QueueFilter[] = ['all', 'unassigned', 'today', 'stale', 'duplicates'];
const VALID_SOURCES: SourceFilter[] = ['all', 'facebook_api', 'facebook_sheets', 'facebook_leads', 'web'];
const VALID_PAGE_SIZES = [25, 50, 100];

const EMPTY_SUMMARY: LeadMetrics['summary'] = {
  total: 0,
  new_leads: 0,
  pending: 0,
  information: 0,
  success: 0,
  unassigned: 0,
  sms_sent: 0,
  sms_failed: 0,
  viewed: 0,
  today: 0,
  stale_pending: 0,
  duplicates: 0,
  avg_assignment_hours: 0,
};

const readEnumParam = <T extends string>(value: string | null, allowed: readonly T[], fallback: T): T =>
  value && allowed.includes(value as T) ? value as T : fallback;

const readPageParam = (value: string | null): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

const parseLeadTimestamp = (value: string): Date => {
  if (!value) return new Date(0);
  return new Date(value.includes('T') ? value : value.replace(' ', 'T'));
};

function LeadListSkeleton() {
  return (
    <div className="space-y-3" aria-label="Φόρτωση επαφών">
      {Array.from({ length: 5 }).map((_, index) => (
        <Card key={index} className="rounded-2xl border-slate-200/80">
          <CardContent className="p-4 sm:p-5">
            <div className="flex gap-3">
              <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-10 w-28" />
                </div>
                <div className="flex gap-2">
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-28" />
                  <Skeleton className="hidden h-5 w-32 sm:block" />
                </div>
                <Skeleton className="h-4 w-full max-w-lg" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function Leads() {
  const { deleteLead } = useLeads();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get('search')?.trim() || '');
  const [stageFilter, setStageFilter] = useState<LeadStageType | 'all'>(() =>
    readEnumParam(searchParams.get('stage'), ['all', ...VALID_STAGES], 'all'),
  );
  const [serviceFilter, setServiceFilter] = useState<ServiceType | 'all'>(() =>
    (searchParams.get('service') as ServiceType | null) || 'all',
  );
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>(() =>
    readEnumParam(searchParams.get('source'), VALID_SOURCES, 'all'),
  );
  const [campaignFilter, setCampaignFilter] = useState(() => searchParams.get('campaign') || 'all');
  const [queueFilter, setQueueFilter] = useState<QueueFilter>(() =>
    readEnumParam(searchParams.get('queue'), VALID_QUEUES, 'all'),
  );
  const [sortBy, setSortBy] = useState<SortOrder>(() =>
    readEnumParam(searchParams.get('sort'), ['newest', 'oldest'], 'newest'),
  );
  const [currentPage, setCurrentPage] = useState(() => readPageParam(searchParams.get('page')));
  const [pageSize, setPageSize] = useState(() => {
    const candidate = Number(searchParams.get('per_page'));
    return VALID_PAGE_SIZES.includes(candidate) ? candidate : 25;
  });
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<AdminLead | null>(null);
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedSearch) next.set('search', debouncedSearch);
    if (stageFilter !== 'all') next.set('stage', stageFilter);
    if (serviceFilter !== 'all') next.set('service', serviceFilter);
    if (sourceFilter !== 'all') next.set('source', sourceFilter);
    if (campaignFilter !== 'all') next.set('campaign', campaignFilter);
    if (queueFilter !== 'all') next.set('queue', queueFilter);
    if (sortBy !== 'newest') next.set('sort', sortBy);
    if (currentPage > 1) next.set('page', String(currentPage));
    if (pageSize !== 25) next.set('per_page', String(pageSize));
    setSearchParams(next, { replace: true });
  }, [campaignFilter, currentPage, debouncedSearch, pageSize, queueFilter, setSearchParams, serviceFilter, sortBy, sourceFilter, stageFilter]);

  const listQuery = useMemo<AdminLeadQuery>(() => ({
    page: currentPage,
    perPage: pageSize,
    search: debouncedSearch,
    stage: stageFilter,
    service: serviceFilter,
    source: sourceFilter,
    campaign: campaignFilter,
    queue: queueFilter,
    sort: sortBy,
  }), [campaignFilter, currentPage, debouncedSearch, pageSize, queueFilter, serviceFilter, sortBy, sourceFilter, stageFilter]);

  const leadsQuery = useQuery({
    queryKey: ['admin-leads', listQuery],
    queryFn: () => fetchAdminLeads(listQuery),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
  const metricsQuery = useQuery({
    queryKey: ['admin-lead-metrics', 'all'],
    queryFn: () => fetchLeadMetrics('all'),
    staleTime: 30_000,
  });
  const followUpPreviewQuery = useQuery({
    queryKey: ['admin-follow-up-preview', 3],
    queryFn: () => previewFollowUpReminders(3),
    enabled: showFollowUpDialog,
    staleTime: 0,
    retry: false,
  });

  const leads = leadsQuery.data?.leads || [];
  const campaigns = leadsQuery.data?.campaigns || [];
  const pagination = leadsQuery.data?.pagination || {
    page: currentPage,
    per_page: pageSize,
    total: 0,
    total_pages: 1,
  };
  const summary = metricsQuery.data?.summary || EMPTY_SUMMARY;
  const assigned = Math.max(0, summary.total - summary.unassigned);
  const assignmentCoverage = summary.total > 0
    ? Math.floor((assigned / summary.total) * 1_000) / 10
    : 100;
  const metricsUnavailable = metricsQuery.isError;

  useEffect(() => {
    if (!leadsQuery.isPlaceholderData && currentPage > pagination.total_pages) {
      setCurrentPage(Math.max(1, pagination.total_pages));
    }
  }, [currentPage, leadsQuery.isPlaceholderData, pagination.total_pages]);

  const activeFilterCount = [
    debouncedSearch !== '',
    stageFilter !== 'all',
    serviceFilter !== 'all',
    sourceFilter !== 'all',
    campaignFilter !== 'all',
    queueFilter !== 'all',
    sortBy !== 'newest',
  ].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;

  const changeQueue = (queue: QueueFilter) => {
    setQueueFilter(queue);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setStageFilter('all');
    setServiceFilter('all');
    setSourceFilter('all');
    setCampaignFilter('all');
    setQueueFilter('all');
    setSortBy('newest');
    setCurrentPage(1);
  };

  const isStale = (lead: AdminLead) =>
    lead.stage === 'σεΕκκρεμότητα' &&
    (Date.now() - parseLeadTimestamp(lead.updatedAt).getTime()) / 3_600_000 >= 48;

  const deleteMutation = useMutation({
    mutationFn: async (lead: AdminLead) => deleteLead(lead.id),
    onSuccess: async (_, lead) => {
      setLeadToDelete(null);
      toast({ title: 'Η επαφή διαγράφηκε', description: `${lead.name} αφαιρέθηκε οριστικά από το CRM.` });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-leads'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-lead-metrics'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-operations'] }),
      ]);
    },
    onError: (error) => toast({
      title: 'Η διαγραφή δεν ολοκληρώθηκε',
      description: error instanceof Error ? error.message : 'Δοκιμάστε ξανά.',
      variant: 'destructive',
    }),
  });

  const followUpMutation = useMutation({
    mutationFn: () => sendFollowUpReminders(3),
    onSuccess: (result) => {
      setShowFollowUpDialog(false);
      queryClient.removeQueries({ queryKey: ['admin-follow-up-preview', 3] });
      toast({
        title: result.sent ? `Εστάλησαν ${result.sent} υπενθυμίσεις` : 'Δεν υπήρχαν επιλέξιμες επαφές',
        description: result.errors
          ? `${result.errors} email απέτυχαν και χρειάζονται έλεγχο.`
          : 'Η αποστολή ολοκληρώθηκε.',
        variant: result.errors ? 'destructive' : 'default',
      });
    },
    onError: (error) => toast({
      title: 'Η αποστολή δεν ολοκληρώθηκε',
      description: error instanceof Error ? error.message : 'Δοκιμάστε ξανά.',
      variant: 'destructive',
    }),
  });

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      await exportAdminLeadsCsv(listQuery);
      toast({
        title: 'Η εξαγωγή ξεκίνησε',
        description: `${pagination.total} επαφές με τα τρέχοντα φίλτρα.`,
      });
    } catch (error) {
      toast({
        title: 'Η εξαγωγή δεν ολοκληρώθηκε',
        description: error instanceof Error ? error.message : 'Δοκιμάστε ξανά.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const refreshData = async () => {
    await Promise.all([leadsQuery.refetch(), metricsQuery.refetch()]);
    toast({ title: 'Η λίστα ενημερώθηκε', description: 'Τα leads και οι μετρήσεις φορτώθηκαν ξανά από τον server.' });
  };

  const goToPage = (page: number) => {
    setCurrentPage(Math.min(pagination.total_pages, Math.max(1, page)));
    window.requestAnimationFrame(() => document.getElementById('lead-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const queueTabs: Array<{ key: QueueFilter; label: string; count: number; alert?: boolean; icon?: typeof CalendarDays }> = [
    { key: 'all', label: 'Όλες', count: summary.total },
    { key: 'today', label: 'Σήμερα', count: summary.today, icon: CalendarDays },
    { key: 'unassigned', label: 'Χωρίς συνεργάτη', count: summary.unassigned, alert: summary.unassigned > 0 },
    { key: 'stale', label: 'Εκκρεμούν 48h+', count: summary.stale_pending, alert: summary.stale_pending > 0 },
    { key: 'duplicates', label: 'Διπλότυπα', count: summary.duplicates },
  ];

  const statsValue = (value: number) => metricsQuery.isPending || metricsUnavailable ? '—' : value;

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        title="Επαφές"
        description="Η καθημερινή ουρά όλων των leads, με άμεση εικόνα ανάθεσης, ηλικίας και προέλευσης."
        eyebrow="CRM"
        icon={<UserRound />}
        iconGradient="purple"
        actions={
          <>
            <Link to="/admin/leads/new" className="flex-1 sm:flex-none">
              <Button variant="gradient" className="h-11 w-full px-4 sm:w-auto">
                <Plus className="mr-2 h-4 w-4" />
                Νέα επαφή
              </Button>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-11 flex-1 border-slate-200 px-4 sm:flex-none">
                  <Settings2 className="mr-2 h-4 w-4" />
                  Εργαλεία
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Διαχείριση leads</DropdownMenuLabel>
                <DropdownMenuItem asChild className="min-h-10">
                  <Link to="/admin/leads/statistics"><BarChart3 className="mr-2 h-4 w-4" />Στατιστικά επαφών</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-10">
                  <Link to="/admin/leads/assignment"><UsersRound className="mr-2 h-4 w-4" />Κανόνες και ανάθεση</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => void handleExportCsv()} disabled={isExporting} className="min-h-10">
                  {isExporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  Εξαγωγή τρέχοντος CSV
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setShowFollowUpDialog(true)} className="min-h-10 text-amber-700 focus:text-amber-800">
                  <Bell className="mr-2 h-4 w-4" />Email υπενθυμίσεων
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <StatCard
          value={statsValue(summary.today)}
          title="Νέα σήμερα"
          description="Παραλαβές από όλες τις πηγές"
          icon={<CalendarDays />}
          variant="info"
          size="compact"
        />
        <StatCard
          value={statsValue(summary.unassigned)}
          title="Χωρίς συνεργάτη"
          description={summary.unassigned > 0 ? 'Χρειάζονται χειροκίνητο έλεγχο' : 'Καμία εκκρεμότητα ανάθεσης'}
          icon={<AlertTriangle />}
          variant={summary.unassigned > 0 ? 'warning' : 'success'}
          size="compact"
        />
        <StatCard
          value={statsValue(summary.stale_pending)}
          title="Χωρίς εξέλιξη 48h+"
          description="Βάσει τελευταίας αλλαγής"
          icon={<Bell />}
          variant={summary.stale_pending > 0 ? 'danger' : 'success'}
          size="compact"
        />
        <StatCard
          value={metricsQuery.isPending || metricsUnavailable
            ? '—'
            : `${assignmentCoverage.toLocaleString('el-GR', { maximumFractionDigits: 1 })}%`}
          title="Κάλυψη ανάθεσης"
          description={`${assigned.toLocaleString('el-GR')} από ${summary.total.toLocaleString('el-GR')} leads`}
          icon={<CheckCircle2 />}
          variant={assignmentCoverage >= 95 ? 'success' : assignmentCoverage >= 80 ? 'warning' : 'danger'}
          size="compact"
        />
      </div>

      {metricsUnavailable && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="status">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Οι συνολικές μετρήσεις δεν φορτώθηκαν. Η λίστα παραμένει διαθέσιμη και μπορείτε να δοκιμάσετε ανανέωση.
        </div>
      )}

      <Card className="overflow-hidden rounded-2xl border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
        <CardContent className="p-0">
          <div className="border-b border-slate-200/80 bg-slate-50/60 px-4 py-4 sm:px-5">
            <div className="mb-2.5 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Ουρά εργασίας</p>
                <p className="text-xs text-slate-500">Επιλέξτε τι χρειάζεται προσοχή τώρα.</p>
              </div>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-blue-700">
                  <X className="mr-1.5 h-4 w-4" />Καθαρισμός
                </Button>
              )}
            </div>
            <div className="-mx-4 overflow-x-auto px-4 pb-1 sm:-mx-5 sm:px-5">
              <div className="flex min-w-max gap-2">
                {queueTabs.map(({ key, label, count, alert, icon: QueueIcon }) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={queueFilter === key}
                    onClick={() => changeQueue(key)}
                    className={cn(
                      'inline-flex min-h-11 items-center gap-2 whitespace-nowrap rounded-xl border px-3.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                      queueFilter === key
                        ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900',
                    )}
                  >
                    {QueueIcon && <QueueIcon className="h-4 w-4" />}
                    {alert && <AlertTriangle className={cn('h-4 w-4', queueFilter === key ? 'text-amber-300' : 'text-amber-600')} />}
                    {label}
                    <span className={cn(
                      'rounded-full px-2 py-0.5 text-xs tabular-nums',
                      queueFilter === key ? 'bg-white/15 text-white' : alert ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600',
                    )}>
                      {metricsQuery.isPending || metricsUnavailable ? '—' : count.toLocaleString('el-GR')}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4 p-4 sm:p-5">
            <div className="flex flex-col gap-3 md:flex-row">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Όνομα, τηλέφωνο, email, διεύθυνση ή Τ.Κ."
                  aria-label="Αναζήτηση επαφών"
                  className="h-11 border-slate-200 bg-white pl-10 pr-10"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Καθαρισμός αναζήτησης"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowMobileFilters((current) => !current)}
                className="h-11 justify-center border-slate-200 md:hidden"
                aria-expanded={showMobileFilters}
              >
                <Filter className="mr-2 h-4 w-4" />
                Φίλτρα{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </Button>
            </div>

            <div className={cn('gap-3 md:grid md:grid-cols-2 xl:grid-cols-5', showMobileFilters ? 'grid' : 'hidden')}>
              <Select value={stageFilter} onValueChange={(value) => { setStageFilter(value as LeadStageType | 'all'); setCurrentPage(1); }}>
                <SelectTrigger className="h-11 w-full border-slate-200 bg-slate-50"><SelectValue placeholder="Στάδιο" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλα τα στάδια</SelectItem>
                  <SelectItem value="Νέο">Νέο</SelectItem>
                  <SelectItem value="σεΕκκρεμότητα">Σε εκκρεμότητα</SelectItem>
                  <SelectItem value="έκλεισεΗΔουλειά">Έκλεισε η δουλειά</SelectItem>
                  <SelectItem value="ήθελεΠληροφορίες">Ήθελε πληροφορίες</SelectItem>
                </SelectContent>
              </Select>
              <Select value={serviceFilter} onValueChange={(value) => { setServiceFilter(value as ServiceType | 'all'); setCurrentPage(1); }}>
                <SelectTrigger className="h-11 w-full border-slate-200 bg-slate-50"><SelectValue placeholder="Υπηρεσία" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες οι υπηρεσίες</SelectItem>
                  {SERVICE_OPTIONS.map(({ value, label }) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={sourceFilter} onValueChange={(value) => { setSourceFilter(value as SourceFilter); setCurrentPage(1); }}>
                <SelectTrigger className="h-11 w-full border-slate-200 bg-slate-50">
                  <Globe className="mr-2 h-4 w-4 text-slate-400" /><SelectValue placeholder="Πηγή" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες οι πηγές</SelectItem>
                  <SelectItem value="facebook_api">Meta API · Realtime</SelectItem>
                  <SelectItem value="facebook_sheets">Facebook Sheets · Ιστορικά</SelectItem>
                  <SelectItem value="facebook_leads">Facebook Ads</SelectItem>
                  <SelectItem value="web">Web form</SelectItem>
                </SelectContent>
              </Select>
              <Select value={campaignFilter} onValueChange={(value) => { setCampaignFilter(value); setCurrentPage(1); }}>
                <SelectTrigger className="h-11 w-full min-w-0 border-slate-200 bg-slate-50">
                  <Megaphone className="mr-2 h-4 w-4 shrink-0 text-slate-400" /><SelectValue placeholder="Καμπάνια" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλες οι καμπάνιες</SelectItem>
                  {campaigns.map((campaign) => <SelectItem key={campaign} value={campaign}>{campaign}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={(value) => { setSortBy(value as SortOrder); setCurrentPage(1); }}>
                <SelectTrigger className="h-11 w-full border-slate-200 bg-slate-50"><SelectValue placeholder="Ταξινόμηση" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Νεότερα πρώτα</SelectItem>
                  <SelectItem value="oldest">Παλαιότερα πρώτα</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <section id="lead-results" className="scroll-mt-24 space-y-3" aria-labelledby="lead-results-title">
        <div className="flex flex-col gap-3 px-1 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 id="lead-results-title" className="text-base font-semibold text-slate-900">Αποτελέσματα</h2>
              {leadsQuery.isFetching && !leadsQuery.isPending && <Loader2 className="h-4 w-4 animate-spin text-slate-400" aria-label="Ανανέωση" />}
            </div>
            <p className="mt-0.5 text-sm text-slate-500">
              {leadsQuery.isPending ? 'Φόρτωση…' : `${pagination.total.toLocaleString('el-GR')} επαφές με τα τρέχοντα φίλτρα`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-slate-500 sm:inline">Ανά σελίδα</span>
            <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setCurrentPage(1); }}>
              <SelectTrigger className="h-10 w-20 border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>{VALID_PAGE_SIZES.map((size) => <SelectItem key={size} value={String(size)}>{size}</SelectItem>)}</SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => void refreshData()}
              disabled={leadsQuery.isFetching || metricsQuery.isFetching}
              className="h-10 w-10 border-slate-200"
              aria-label="Ανανέωση λίστας"
            >
              <RefreshCw className={cn('h-4 w-4', (leadsQuery.isFetching || metricsQuery.isFetching) && 'animate-spin')} />
            </Button>
          </div>
        </div>

        {leadsQuery.isPending ? (
          <LeadListSkeleton />
        ) : leadsQuery.isError ? (
          <Card className="rounded-2xl border-rose-200 bg-rose-50/50">
            <CardContent className="flex flex-col items-center gap-4 px-5 py-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-700"><AlertCircle className="h-6 w-6" /></div>
              <div>
                <h3 className="font-semibold text-slate-950">Δεν φορτώθηκαν οι επαφές</h3>
                <p className="mt-1 text-sm text-slate-600">{leadsQuery.error instanceof Error ? leadsQuery.error.message : 'Παρουσιάστηκε σφάλμα API.'}</p>
              </div>
              <Button variant="outline" onClick={() => void leadsQuery.refetch()} className="h-11 border-rose-200 bg-white">
                <RefreshCw className="mr-2 h-4 w-4" />Δοκιμή ξανά
              </Button>
            </CardContent>
          </Card>
        ) : leads.length === 0 ? (
          <EmptyState
            icon={<UserRound />}
            illustration="lead-flow"
            title="Δεν βρέθηκαν επαφές"
            description={hasActiveFilters ? 'Δεν υπάρχει lead που να ταιριάζει σε αυτά τα φίλτρα.' : 'Δεν υπάρχουν ακόμη leads στο CRM.'}
            action={hasActiveFilters ? <Button variant="outline" onClick={clearFilters}>Καθαρισμός φίλτρων</Button> : undefined}
          />
        ) : (
          <div className={cn('space-y-3 transition-opacity', leadsQuery.isPlaceholderData && 'opacity-60')}>
            {leads.map((lead, index) => {
              const stale = isStale(lead);
              return (
                <div
                  key={lead.id}
                  className={cn('animate-slide-up overflow-hidden rounded-xl', stale && 'ring-1 ring-rose-200 shadow-sm shadow-rose-100/60')}
                  style={{ animationDelay: `${Math.min(index, 10) * 20}ms` }}
                >
                  {stale && (
                    <div className="flex items-center gap-2 border-b border-rose-100 bg-rose-50 px-3.5 py-2 text-xs font-medium text-rose-800 sm:px-4">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      Χωρίς αλλαγή σταδίου για περισσότερες από 48 ώρες
                    </div>
                  )}
                  <LeadCard lead={lead} isDuplicate={lead.isDuplicate}>
                    <Link to={`/admin/lead/${lead.id}`} className="flex-1 sm:flex-none">
                      <Button size="sm" className="h-10 w-full gap-2 bg-slate-900 px-4 hover:bg-slate-800 sm:w-auto">
                        <Eye className="h-4 w-4" />Προβολή
                      </Button>
                    </Link>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" className="h-10 w-10 shrink-0 border-slate-200" aria-label={`Περισσότερες ενέργειες για ${lead.name}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onSelect={() => setLeadToDelete(lead)} className="min-h-10 text-rose-700 focus:text-rose-700">
                          <Trash2 className="mr-2 h-4 w-4" />Οριστική διαγραφή
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </LeadCard>
                </div>
              );
            })}
          </div>
        )}

        {!leadsQuery.isPending && !leadsQuery.isError && pagination.total_pages > 1 && (
          <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-center text-sm text-slate-500 sm:text-left">
              Σελίδα <span className="font-semibold text-slate-800">{pagination.page}</span> από{' '}
              <span className="font-semibold text-slate-800">{pagination.total_pages}</span>
            </p>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <Button variant="outline" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1 || leadsQuery.isFetching} className="h-11 border-slate-200">
                <ChevronLeft className="mr-1.5 h-4 w-4" />Προηγούμενη
              </Button>
              <Button variant="outline" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= pagination.total_pages || leadsQuery.isFetching} className="h-11 border-slate-200">
                Επόμενη<ChevronRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </section>

      <Dialog open={Boolean(leadToDelete)} onOpenChange={(open) => { if (!open && !deleteMutation.isPending) setLeadToDelete(null); }}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Οριστική διαγραφή επαφής</DialogTitle>
            <DialogDescription>
              Θα διαγραφεί το lead «{leadToDelete?.name}» μαζί με τα σχετικά SMS logs. Η ενέργεια δεν αναιρείται.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-800">
            Χρησιμοποιήστε τη διαγραφή μόνο για πραγματικά άκυρες εγγραφές. Για επιχειρησιακή εκκρεμότητα αλλάξτε στάδιο ή ανάθεση.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeadToDelete(null)} disabled={deleteMutation.isPending}>Ακύρωση</Button>
            <Button variant="destructive" onClick={() => leadToDelete && deleteMutation.mutate(leadToDelete)} disabled={!leadToDelete || deleteMutation.isPending}>
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Διαγραφή οριστικά
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showFollowUpDialog} onOpenChange={(open) => { if (!followUpMutation.isPending) setShowFollowUpDialog(open); }}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Email υπενθύμισης συνεργατών</DialogTitle>
            <DialogDescription>
              Ο server θα βρει ανατεθειμένα leads που παραμένουν «Σε εκκρεμότητα» πάνω από 3 ημέρες και θα στείλει email στον αντίστοιχο συνεργάτη.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
            {followUpPreviewQuery.isPending ? (
              <div className="flex items-center gap-2 text-sm text-slate-600"><Loader2 className="h-4 w-4 animate-spin" />Έλεγχος επιλέξιμων επαφών…</div>
            ) : followUpPreviewQuery.isError ? (
              <div className="flex items-start gap-2 text-sm text-rose-700"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />Δεν ήταν δυνατός ο ασφαλής προέλεγχος. Η αποστολή παραμένει απενεργοποιημένη.</div>
            ) : (
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-700">Επιλέξιμες επαφές</p>
                  <p className="mt-0.5 text-xs text-slate-500">Μόνο με ενεργή ανάθεση και email συνεργάτη</p>
                </div>
                <span className="text-2xl font-bold tabular-nums text-slate-950">{followUpPreviewQuery.data?.eligible ?? 0}</span>
              </div>
            )}
          </div>
          <p className="text-xs leading-5 text-slate-500">Η ενέργεια στέλνει πραγματικά email. Δεν αλλάζει στάδιο, ανάθεση ή δεδομένα του lead.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFollowUpDialog(false)} disabled={followUpMutation.isPending}>Ακύρωση</Button>
            <Button
              onClick={() => followUpMutation.mutate()}
              disabled={followUpMutation.isPending || followUpPreviewQuery.isPending || followUpPreviewQuery.isError || !followUpPreviewQuery.data?.eligible}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              {followUpMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bell className="mr-2 h-4 w-4" />}
              Αποστολή email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
